<template>
  <app-header></app-header>
  <div class="cart">
    <component
      :cart-data="cartData"
      :is="currentComponent"
      :phone-server-check="phoneServerCheck"
      :formatNumber="formatNumber"
      @update-data="handleCartDataUpdate"
      @submit-order="submitOrder"
      @next="nextStep"
      @prev="prevStep"
    ></component>
  </div>
  <app-footer></app-footer>
  <Loading v-show="loading"></Loading>
</template>

<script>
import AppHeader from '@/components/Header.vue';
import AppFooter from '@/components/Footer.vue';
import CartComponent from '@/components/cartCheckout/CartComponentStep01.vue';
import PaymentComponent from '@/components/cartCheckout/PaymentComponentStep02.vue';
import { CUSTOM_ERROR_CODE } from '@/utils/validation.js';
import { querySessionPacks, createOrder, createAuthOrder } from '@/api/cartService.js';
import { getUserInfo } from '@/api/apiService';
import { setCartItems, getCartItems } from '@/localStorage/cartStorage.js';
import { formatTimeRange } from '@/utils/formatTimeRange';
import Loading from '@/components/Loading.vue';

export default {
  name: 'ShoppingCart',
  components: {
    'app-header': AppHeader,
    'app-footer': AppFooter,
    CartComponent,
    PaymentComponent,
    Loading,
  },
  data() {
    return {
      phoneServerCheck: true,
      step: 0,
      cartData: {
        orderItems: [],
        totalAmount: 0,
        totalDiscount: 0,
        finalAmount: 0,
        name: '',
        phone: '',
        email: '',
        birth: '',
        address: {
          county: '',
          zipCode: '',
          district: '',
          addressDetail: '',
        },
        hasPhysicalItem: null,
        shipping: null,
        taxId: '',
        ageCheckSelection: null,
        needLogin: null,
      },
      loading: false,
    };
  },
  async created() {
    await this.getCartData();
    if (this.jwt) {
      await this.getUserInfo();
    }
  },
  computed: {
    jwt() {
      return this.$store.state.jwt;
    },
    currentComponent() {
      switch (this.step) {
        case 0:
          return 'CartComponent';
        case 1:
          return 'PaymentComponent';
        default:
          return 'CartComponent';
      }
    },
  },
  watch: {
    'cartData.orderItems': {
      handler(newItems) {
        const { totalAmount, totalDiscount, finalAmount } = newItems.reduce(
          (result, product) => {
            const amount = product.originalPrice || product.price;
            const discount = product.originalPrice ? product.originalPrice - product.price : 0;

            // 计算当前产品的金额
            const productTotal = amount * product.quantity;
            const productDiscount = discount * product.quantity;
            const productFinal = product.price * product.quantity;

            // 计算 addOnItems 的总金额
            const addOnTotals = product.addOnItems.reduce(
              (acc, addOnItem) => {
                const addOnAmount = addOnItem.originalPrice || addOnItem.price;
                const addOnDiscount = addOnItem.originalPrice ? addOnItem.originalPrice - addOnItem.price : 0;

                // 更新附加商品的金额
                acc.totalAmount += addOnAmount * addOnItem.quantity;
                acc.totalDiscount += addOnDiscount * addOnItem.quantity;
                acc.finalAmount += addOnItem.price * addOnItem.quantity;

                return acc;
              },

              { totalAmount: 0, totalDiscount: 0, finalAmount: 0 },
            );

            return {
              totalAmount: result.totalAmount + productTotal + addOnTotals.totalAmount,
              totalDiscount: result.totalDiscount + productDiscount + addOnTotals.totalDiscount,
              finalAmount: result.finalAmount + productFinal + addOnTotals.finalAmount,
            };
          },
          {
            totalAmount: 0,
            totalDiscount: 0,
            finalAmount: 0,
          },
        );

        this.cartData.totalAmount = totalAmount;
        this.cartData.totalDiscount = totalDiscount;
        this.cartData.finalAmount = finalAmount;
        this.handlePhysicalItemChange();
      },
      deep: true,
    },

    jwt() {
      if (this.jwt) {
        this.getUserInfo();
      }
    },
  },
  methods: {
    async getUserInfo() {
      try {
        this.loading = true;

        const userData = await getUserInfo(this.jwt, true);

        this.cartData.email = userData.contactEmail;
        this.cartData.name = userData.name;
        this.cartData.address = userData.address;
        this.cartData.phone = userData.phone;
        this.cartData.birth = userData.birth;
      } catch (error) {
        if (error?.response?.status === 400) {
          // todo: redirect?
          alert('Failed to get user info');
          console.error('account not found');
        } else {
          console.error('error', error.message);
        }
      } finally {
        this.loading = false;
      }
    },
    // get from localStorage
    async getCartData() {
      const cartItems = getCartItems() || [];

      if (Object.keys(cartItems).length === 0) {
        this.$nextTick(() => {
          this.checkCartEmpty();
        });
        return;
      }
      await this.fetchCartItems(cartItems);
    },
    //get from API cartItems Data
    async fetchCartItems(cartItems) {
      try {
        this.loading = true;
        const langSetting = this.currentLanguage === 'zh_tw' ? 'zh-tw' : this.currentLanguage;
        const response = await querySessionPacks(cartItems, langSetting);
        this.fetchCartItemsData = response.data.data;

        this.checkNeedLogin(this.fetchCartItemsData);

        if (this.fetchCartItemsData) {
          this.fetchCartItemsData.forEach((item) => {
            if (item.startTime && item.endTime) {
              item.time = formatTimeRange(item.startTime, item.endTime, false, true);
            }
          });
        }

        const fetchedCarItemsWithQuantity = this.setQuantityToFetchedCartItem(cartItems, this.fetchCartItemsData);
        this.cartData.orderItems = fetchedCarItemsWithQuantity;

        this.checkStockAndUpdate();
      } catch (error) {
        console.error('Error fetching CartItems:', error);
      } finally {
        this.loading = false;
      }
    },
    checkNeedLogin(data) {
      this.cartData.needLogin = data.some((item) => item.needLogin);
    },
    checkStockAndUpdate() {
      const insufficientItems = []; // 用於收集庫存不足的商品

      // 遍歷購物車商品
      this.cartData.orderItems.forEach((item) => {
        // 檢查庫存狀況
        if (item.remainingQuantity !== -1 && item.quantity > item.remainingQuantity) {
          insufficientItems.push({
            ...item,
          });
        }
      });

      // 如果有庫存不足的商品
      if (insufficientItems.length > 0) {
        const alertMessage = insufficientItems.map((item) => ` ${item.productName} ${item.packTitle} ${this.$t('shopping-cart.key044')} ${item.remainingQuantity}`).join('\n');

        alert(`${alertMessage}`);
        this.updateLocalStorage(insufficientItems);
      }
    },
    updateLocalStorage(insufficientItems) {
      this.cartData.orderItems = this.cartData.orderItems.filter((item) => {
        const isInsufficient = insufficientItems.some((insItem) => insItem.productId === item.productId && insItem.sessionId === item.sessionId && insItem.packId === item.packId);
        return !isInsufficient;
      });

      // 準備要保存到 localStorage 的 cartItems
      let cartItems = this.cartData.orderItems.map((item) => ({
        productId: item.productId,
        sessionId: item.sessionId,
        packId: item.packId,
        quantity: item.quantity,
      }));

      // 保存到 localStorage
      setCartItems(cartItems);
      this.checkCartEmpty();
    },
    handlePhysicalItemChange() {
      const hasPhysical = this.cartData.orderItems.some((item) => {
        if (item.hasPhysicalItem) {
          return true;
        }
        return item.addOnItems && item.addOnItems.length > 0 && item.addOnItems.some((addOnItem) => addOnItem.physicalItems && addOnItem.physicalItems.length > 0);
      });

      this.cartData.hasPhysicalItem = hasPhysical;
      if (!hasPhysical) {
        this.cartData.shipping = null;
      }
    },
    setQuantityToFetchedCartItem(cartItems, fetchCartItemsData) {
      const cartItemsMap = new Map();
      cartItems.forEach((item) => {
        const key = `${item.productId}-${item.sessionId}-${item.packId}`;
        cartItemsMap.set(key, item.quantity);
      });

      if (Array.isArray(fetchCartItemsData)) {
        fetchCartItemsData.forEach((data) => {
          const key = `${data.productId}-${data.sessionId}-${data.packId}`;
          if (cartItemsMap.has(key)) {
            data.quantity = cartItemsMap.get(key); // 設置數量
          } else {
            data.quantity = 0;
          }
        });

        return fetchCartItemsData;
      } else {
        console.error('fetchCartItemsData is not an array:', fetchCartItemsData);
        this.fetchCartItemsData = [];
      }
    },
    //Amount format
    formatNumber(value) {
      return value.toLocaleString('en-US');
    },
    updateQuantity(itemId, quantity) {
      this.cartService.updateCartItemQuantity(itemId, quantity);
    },
    removeFromCart(itemId) {
      this.cartService.removeFromCart(itemId);
    },
    nextStep() {
      this.step += 1;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    prevStep() {
      this.step = 0;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    updateData(newData) {
      this.cartData = newData;
    },
    handleCartDataUpdate(updatedCartData) {
      this.cartData = updatedCartData;
      this.checkNeedLogin(updatedCartData.orderItems);
    },
    checkCartEmpty() {
      if (this.cartData.orderItems.length === 0) {
        this.loading = false;
        alert(this.$t('shopping-cart.key020'));
        this.$router.push(`/${this.currentLanguage}/`);
      }
    },
    async submitOrder() {
      let response;
      try {
        this.loading = true;
        const langSetting = this.currentLanguage === 'zh_tw' ? 'zh-tw' : this.currentLanguage;

        const sendBody = {
          ...this.cartData,
          phone: this.cartData.phone.replace(/\s/g, ''),
        };

        response = this.jwt ? await createAuthOrder(sendBody, langSetting, this.jwt) : await createOrder(sendBody, langSetting);

        if (response.data) {
          this.submitToPaymentGateway(response.data.data);
        }

        this.phoneServerCheck = true;
      } catch (error) {
        console.error('Error submitting order:', error);
        if (error?.response?.status === 422) {
          if (error?.response.data.data.customCode === CUSTOM_ERROR_CODE.INVALID_TAX_ID) {
            alert(this.$t('shopping-cart.key046'));
          } else if (error?.response.data.data.customCode === CUSTOM_ERROR_CODE.INVALID_PHONE) {
            alert(this.$t('shopping-cart.key047'));
            this.phoneServerCheck = false;
          }
        } else if (error?.response?.status === 409) {
          alert(this.$t('shopping-cart.key045'));
          window.location.reload();
        } else {
          alert(this.$t('shopping-cart.key039'));
        }
      } finally {
        this.loading = false;
      }
    },
    submitToPaymentGateway(data) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = process.env.NEWEB_PAY_URL;

      const fields = [
        { name: 'MerchantID', value: data.merchantId },
        { name: 'Version', value: data.version },
        { name: 'TradeInfo', value: data.tradeInfo },
        { name: 'TradeSha', value: data.tradeSha },
      ];

      fields.forEach((field) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    },
  },
};
</script>

<style lang="scss">
@import '../assets/styles/pages/products/cart.scss';
</style>
